import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Card } from '@/lib';
import { useWorkspace } from '@/features/workspaces/hooks';
import {
  GetMarketIntelligenceReportResponse,
  MarketIntelligenceReportStatus,
  useLazyGetMarketIntelligenceReportQuery,
} from '@/services/endpoints/marketIntelligence';
import { MarketIntelligenceReportFailure } from '../types';
import { extractMarketIntelligenceErrorMessage } from '../utils';

interface ReportGeneratingScreenProps {
  reportId?: string | null;
  onComplete?: (response: GetMarketIntelligenceReportResponse) => void;
  onError?: (failure: MarketIntelligenceReportFailure) => void;
}

const POLL_INTERVAL_MS = 60000;
const MAX_POLLING_DURATION_MS = 10 * 60 * 1000;
const COMPLETION_DELAY_MS = 2000;
const ANIMATED_DOTS = [
  { id: 'dot-1', value: '.', delay: 0 },
  { id: 'dot-2', value: '.', delay: 200 },
  { id: 'dot-3', value: '.', delay: 400 },
] as const;
const GENERATING_STEP_KEYS = [
  'generating.steps.websiteScan',
  'generating.steps.competitorDiscovery',
  'generating.steps.trafficAnalysis',
  'generating.steps.positioningGap',
  'generating.steps.strategicActionsSummary',
] as const;

const STATUS_TO_STEP_INDEX: Record<MarketIntelligenceReportStatus, number> = {
  PENDING: 0,
  RUNNING_STEP1: 1,
  RUNNING_STEP2: 2,
  RUNNING_STEP3: 3,
  DONE: 4,
  ERROR: 0,
};

export const ReportGeneratingScreen: FC<ReportGeneratingScreenProps> = ({ reportId, onComplete, onError }) => {
  const { t } = useTranslation('marketIntelligence');
  const workspace = useWorkspace();
  const [getMarketIntelligenceReport] = useLazyGetMarketIntelligenceReportQuery();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pollingTimeoutRef = useRef<number | null>(null);
  const completionTimeoutRef = useRef<number | null>(null);
  const generatingTitle = t('generating.title');
  const generatingTitleText = generatingTitle.replace(/\.+$/, '');

  useEffect(() => {
    const clearScheduledTasks = () => {
      if (pollingTimeoutRef.current !== null) {
        window.clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }

      if (completionTimeoutRef.current !== null) {
        window.clearTimeout(completionTimeoutRef.current);
        completionTimeoutRef.current = null;
      }
    };

    clearScheduledTasks();
    setErrorMessage(null);
    setCurrentStepIndex(0);

    if (!reportId) {
      return clearScheduledTasks;
    }

    let isUnmounted = false;
    const startedAt = Date.now();

    const pollReportStatus = async () => {
      if (isUnmounted) {
        return;
      }

      try {
        const response = await getMarketIntelligenceReport({
          workspaceId: workspace.id,
          reportId,
        }).unwrap();

        if (isUnmounted) {
          return;
        }

        setErrorMessage(null);
        const nextStepIndex = STATUS_TO_STEP_INDEX[response.status] ?? 0;
        setCurrentStepIndex(nextStepIndex);

        if (response.status === 'DONE') {
          completionTimeoutRef.current = window.setTimeout(() => {
            if (!isUnmounted) {
              onComplete?.(response);
            }
          }, COMPLETION_DELAY_MS);
          return;
        }

        if (response.status === 'ERROR') {
          if (onError) {
            onError(response);
          } else {
            setErrorMessage(response.errorMessage || t('generating.errors.unexpected'));
          }
          return;
        }

        if (Date.now() - startedAt >= MAX_POLLING_DURATION_MS) {
          if (onError) {
            onError({
              reportId,
              retryCount: 0,
              errorKind: null,
              errorMessage: t('generating.errors.timeout'),
            });
          } else {
            setErrorMessage(t('generating.errors.timeout'));
          }
          return;
        }

        pollingTimeoutRef.current = window.setTimeout(() => {
          void pollReportStatus();
        }, POLL_INTERVAL_MS);
      } catch (error) {
        if (!isUnmounted) {
          const errorMessage = extractMarketIntelligenceErrorMessage(error) || t('generating.errors.unexpected');

          if (onError && reportId) {
            onError({
              reportId,
              retryCount: 0,
              errorKind: null,
              errorMessage,
            });
          } else {
            setErrorMessage(errorMessage);
          }
        }
      }
    };

    void pollReportStatus();

    return () => {
      isUnmounted = true;
      clearScheduledTasks();
    };
  }, [getMarketIntelligenceReport, onComplete, onError, reportId, t, workspace.id]);

  return (
    <Card className='block w-full rounded-lg border-primary-border bg-white p-6 drop-shadow-none'>
      <div className='flex flex-col items-center justify-center py-12'>
        {/* Title and description */}
        <h3 className='mb-2 text-xl font-semibold text-primary-900'>
          <span className='sr-only'>{generatingTitle}</span>
          <span aria-hidden='true' className='inline-flex animate-pulse' style={{ animationDuration: '1s' }}>
            {generatingTitleText}
            <span className='inline-flex w-5 justify-start'>
              {ANIMATED_DOTS.map((dot) => (
                <span key={dot.id} className='animate-pulse' style={{ animationDelay: `${dot.delay}ms`, animationDuration: '1.2s' }}>
                  {dot.value}
                </span>
              ))}
            </span>
          </span>
        </h3>
        <p className='mb-8 max-w-md text-center text-sm text-primary-600'>{t('generating.description')}</p>

        {errorMessage ? (
          <div className='mb-6 w-full max-w-md'>
            <Alert type='ERROR' title={t('generating.errors.title')} description={errorMessage} />
          </div>
        ) : null}

        {/* Steps list */}
        <div className='w-full max-w-md rounded-lg border border-primary-border bg-primary-50 p-4'>
          <div className='space-y-3'>
            {GENERATING_STEP_KEYS.map((stepKey, index) => (
              <div key={stepKey} className='flex items-center gap-3'>
                {index < currentStepIndex ? (
                  <div className='flex items-center justify-center w-5 h-5 rounded-full bg-green-500 flex-shrink-0'>
                    <svg
                      className='w-3 h-3 text-white-100'
                      fill='none'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='3'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path d='M5 13l4 4L19 7'></path>
                    </svg>
                  </div>
                ) : index === currentStepIndex ? (
                  <div className='flex items-center justify-center w-5 h-5 rounded-full bg-brand-600 flex-shrink-0'>
                    <div className='w-2 h-2 rounded-full bg-white-100'></div>
                  </div>
                ) : (
                  <div className='w-5 h-5 rounded-full border-2 border-primary-300 flex-shrink-0'></div>
                )}
                <span
                  className={`text-sm ${
                    index < currentStepIndex ? 'text-primary-600' : index === currentStepIndex ? 'font-semibold text-brand-600' : 'text-primary-500'
                  }`}
                >
                  {t(stepKey)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};
